import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { Modal } from '../../components/common/Modal';
import { DriveFileItem } from '../../lib/gdrive';
import { getDriveFilesDB, saveDriveFileDB, deleteDriveFileDB } from '../../services/db';
import { isSuperAdmin, hasRolePermission } from '../../utils/formatters';
import {
  FolderArchive,
  FileText,
  Download,
  Search,
  Eye,
  ShieldCheck,
  BookOpen,
  Video,
  FileCheck2,
  Plus,
  Trash2,
  Sparkles,
  ExternalLink,
  Lock
} from 'lucide-react';

export const DocumentArchivePage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const isExecutive = Boolean(currentUser && hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari']));

  const [documents, setDocuments] = useState<DriveFileItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Semua');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modal Preview State
  const [selectedDoc, setSelectedDoc] = useState<DriveFileItem | null>(null);

  // New Document Form Modal State (Management Only)
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [newDocName, setNewDocName] = useState<string>('');
  const [newDocLink, setNewDocLink] = useState<string>('');
  const [newDocCategory, setNewDocCategory] = useState<'Audit Receipt' | 'Syllabus PDF' | 'Instructional Video' | 'Meeting Minutes'>('Syllabus PDF');
  const [newDocSize, setNewDocSize] = useState<string>('150.0 KB');

  const loadDocuments = async () => {
    try {
      const files = await getDriveFilesDB();
      setDocuments(files);
    } catch {
      // Fallback
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  // Strict Security & Archival Filter: Exclude confidential meeting minutes, audit receipts, videos, and public posters (merchandise/event graphics)
  const publicDocsOnly = documents.filter((doc) => {
    const isExcluded = doc.category === 'Meeting Minutes' ||
      doc.category === 'Instructional Video' ||
      doc.category === 'Audit Receipt' ||
      doc.category === 'Public Poster' ||
      doc.category === 'Member Photo' ||
      doc.name.toLowerCase().includes('minit') ||
      doc.name.toLowerCase().includes('audit') ||
      doc.name.toLowerCase().includes('video') ||
      doc.name.toLowerCase().includes('poster') ||
      doc.name.toLowerCase().includes('merch-');
    return !isExcluded;
  });

  const filteredDocs = publicDocsOnly.filter((doc) => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.category.toLowerCase().includes(searchQuery.toLowerCase());

    if (selectedCategory === 'Semua') return matchesSearch;
    if (selectedCategory === 'Perlembagaan') return matchesSearch && (doc.name.toLowerCase().includes('perlembagaan') || doc.name.toLowerCase().includes('ros'));
    if (selectedCategory === 'Silibus') return matchesSearch && doc.name.toLowerCase().includes('silibus');
    if (selectedCategory === 'Sandang') return matchesSearch && doc.name.toLowerCase().includes('sandang');

    return matchesSearch;
  });

  // Handle Adding New Document (Management Only)
  const handleAddDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDocName.trim() || !newDocLink.trim()) {
      showToast('error', 'Sila isi nama dokumen dan pautan fail');
      return;
    }

    const newFile: DriveFileItem = {
      id: `gdrive-custom-${Date.now()}`,
      name: newDocName.trim(),
      mimeType: newDocCategory === 'Instructional Video' ? 'video/mp4' : 'application/pdf',
      driveType: 'management',
      webContentLink: newDocLink.trim(),
      category: newDocCategory,
      uploadedAt: new Date().toISOString().split('T')[0],
      size: newDocSize.trim() || '100 KB',
    };

    const updated = await saveDriveFileDB(newFile);
    setDocuments(updated);
    showToast('success', 'Dokumen Berjaya Ditambah', `Fail "${newDocName}" telah disimpan ke arkaib.`);

    setIsAddModalOpen(false);
    setNewDocName('');
    setNewDocLink('');
  };

  // Handle Deleting Document (Management Only)
  const handleDeleteDoc = async (id: string, name: string) => {
    const updated = await deleteDriveFileDB(id);
    setDocuments(updated);
    showToast('info', 'Dokumen Dipadam', `Fail "${name}" telah dikeluarkan daripada arkaib.`);
  };

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Page Header */}
      <PageHeader
        badgeText="Fail & Arkaib Rasmi"
        badgeIcon={FolderArchive}
        title="Dokumen & Rujukan"
      >
        {isExecutive && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-4 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span>Muat Naik Dokumen</span>
          </button>
        )}
      </PageHeader>



      {/* Search & Category Filter Section */}
      <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Category Filter Buttons */}
          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { id: 'Semua', label: 'Semua' },
              { id: 'Perlembagaan', label: 'Perlembagaan & ROS' },
              { id: 'Silibus', label: 'Manual Silibus' },
              { id: 'Sandang', label: 'Sandang Kebesaran' },
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all cursor-pointer ${selectedCategory === cat.id
                  ? 'bg-asmm-red text-white shadow-xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari nama dokumen..."
              className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-medium text-slate-900"
            />
          </div>
        </div>

        {/* Document Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          {filteredDocs.length === 0 ? (
            <div className="col-span-2 p-8 text-center text-slate-500 text-xs font-bold bg-slate-50 rounded-2xl border border-slate-200">
              Tiada dokumen dijumpai untuk padanan carian ini.
            </div>
          ) : (
            filteredDocs.map((doc) => {
              const isPdf = doc.mimeType === 'application/pdf';
              const isVideo = doc.mimeType.startsWith('video');

              return (
                <div
                  key={doc.id}
                  className="p-4 bg-slate-50 hover:bg-white rounded-2xl border border-slate-200 hover:border-slate-300 transition-all space-y-3 flex flex-col justify-between shadow-2xs group"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded border uppercase ${doc.category === 'Syllabus PDF'
                        ? 'bg-amber-100 text-amber-800 border-amber-200'
                        : doc.category === 'Audit Receipt'
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                          : doc.category === 'Instructional Video'
                            ? 'bg-purple-100 text-purple-800 border-purple-200'
                            : 'bg-slate-200 text-slate-700 border-slate-300'
                        }`}>
                        {doc.category}
                      </span>

                      <span className="text-[10px] font-mono text-slate-400">
                        {doc.size} • {doc.uploadedAt}
                      </span>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="p-2.5 bg-white rounded-xl border border-slate-200 text-asmm-red shrink-0 shadow-2xs group-hover:scale-105 transition-transform">
                        {isVideo ? <Video className="w-5 h-5 text-purple-600" /> : isPdf ? <FileText className="w-5 h-5 text-asmm-red" /> : <BookOpen className="w-5 h-5 text-amber-600" />}
                      </div>

                      <div className="space-y-0.5">
                        <h4 className="font-extrabold text-xs text-slate-900 group-hover:text-asmm-red transition-colors leading-snug">
                          {doc.name}
                        </h4>
                        <p className="text-[11px] text-slate-500 line-clamp-2">
                          Dokumen pengiktirafan rasmi terbitan Persatuan Seni Silat Awang Sulung Merah Muda.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-3 border-t border-slate-200/80 flex items-center justify-between">
                    <button
                      onClick={() => setSelectedDoc(doc)}
                      className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 font-extrabold text-xs rounded-xl border border-slate-200 flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <Eye className="w-3.5 h-3.5 text-asmm-red" />
                      <span>Buka & Semak</span>
                    </button>

                    <div className="flex items-center gap-2">
                      <a
                        href={doc.webContentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-2xs flex items-center gap-1.5 cursor-pointer"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Muat Turun</span>
                      </a>

                      {isExecutive && (
                        <button
                          onClick={() => handleDeleteDoc(doc.id, doc.name)}
                          className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-xl border border-rose-200 transition-colors"
                          title="Padam Dokumen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* DOCUMENT PREVIEW MODAL */}
      {selectedDoc && (
        <Modal
          isOpen={!!selectedDoc}
          onClose={() => setSelectedDoc(null)}
          title={`Semakan Dokumen: ${selectedDoc.name}`}
          maxWidth="max-w-3xl"
        >
          <div className="p-6 bg-white text-slate-900 rounded-2xl space-y-6 font-sans">
            <div className="flex items-center justify-between border-b border-slate-200 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-50 text-asmm-red border border-red-200 rounded-xl">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-extrabold text-sm text-slate-900 leading-tight">
                    {selectedDoc.name}
                  </h3>
                  <p className="text-xs text-slate-500 font-mono">Saiz Fail: {selectedDoc.size} • Tarikh: {selectedDoc.uploadedAt}</p>
                </div>
              </div>

            </div>

            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2 text-xs">
              <p className="text-slate-600 leading-relaxed">
                Dokumen ini merupakan fail rasmi di bawah repositori pertubuhan Persatuan Seni Silat Awang Sulung Merah Muda. Anda boleh memuat turun fail lengkap atau membukanya di tetingkap baharu.
              </p>
            </div>

            <div className="pt-3 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setSelectedDoc(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all border border-slate-200"
              >
                Tutup
              </button>

              <a
                href={selectedDoc.webContentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-extrabold rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Buka Fail Penuh (PDF)</span>
              </a>
            </div>
          </div>
        </Modal>
      )}

      {/* ADD NEW DOCUMENT MODAL (Management Only) */}
      {isExecutive && (
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title="Muat Naik Dokumen Baharu Ke Arkaib"
        >
          <form onSubmit={handleAddDocument} className="p-6 bg-white text-slate-900 rounded-2xl space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Tajuk Dokumen / Fail *</label>
              <input
                type="text"
                required
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                placeholder="Contoh: Penyata Kewangan Tahunan 2026.pdf"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-medium text-slate-900"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Pautan Fail / Shareable Link (Google Drive) *</label>
              <input
                type="text"
                required
                value={newDocLink}
                onChange={(e) => setNewDocLink(e.target.value)}
                placeholder="https://drive.google.com/file/d/.../view"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs font-medium text-slate-900 font-mono"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Kategori Dokumen</label>
                <select
                  value={newDocCategory}
                  onChange={(e) => setNewDocCategory(e.target.value as any)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 pr-8"
                >
                  <option value="Syllabus PDF">Silibus & Perlembagaan</option>
                  <option value="Audit Receipt">Penyata ROS & Audit</option>
                  <option value="Meeting Minutes">Minit Mesyuarat</option>
                  <option value="Instructional Video">Video Latihan</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Saiz Fail (Anggaran)</label>
                <input
                  type="text"
                  value={newDocSize}
                  onChange={(e) => setNewDocSize(e.target.value)}
                  placeholder="150.0 KB"
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-medium text-slate-900"
                />
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all border border-slate-200"
              >
                Batal
              </button>

              <button
                type="submit"
                className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-extrabold rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Simpan Dokumen</span>
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};
